import { ApolloError, gql, QueryResult } from '@apollo/client'
import * as React from 'react'
import { copyToClipboard } from '../../../../common/src/clipboard'
import Button from '../../../../common/src/components/Button'
import { Link } from '../../../../common/src/components/Link'
import Table from '../../../../common/src/components/Table'
import TypeaheadBox from '../../../../common/src/components/TypeaheadBox'
import { Spacer, BodyText, IntroText, EllipsisDiv, BodyTextSpan, H4 } from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import { dateMDYFmt } from '../../../../common/src/date'
import { addFeedback } from '../../../../common/src/feedback'
import { formatPhoneNumber } from '../../../../common/src/format'
import memoize from '../../../../common/src/memoize'
import {
  LeasingAttributionSource,
  FetchEndpointsQueryVariables,
  FetchEndpointsQuery,
} from '../../../../common/src/types.gen'
import validate from '../../../../common/src/validate'
import { apolloWebClient } from '../../apolloClientForWeb'
import { PollingQuery } from '../components/PollingQuery'

const endpointsQuery = gql`
  query FetchEndpoints($taskId: Int!, $released: Boolean) {
    task(taskId: $taskId) {
      id
      displayName
      endpoints(released: $released) {
        id
        addr
        releasedAddr
        leasingAttributionSource
        lastInboundTimestamp
        lastOutboundTimestamp
      }
    }
  }
`

const createEndpointMutation = gql`
  mutation CreateEndpoint($taskId: Int!, $leasingAttributionSource: LeasingAttributionSource, $addr: String) {
    createEndpoint(taskId: $taskId, leasingAttributionSource: $leasingAttributionSource, addr: $addr) {
      id
      addressEmail
      addressPhone
      isArchived
      endpoints {
        id
        addr
        releasedAddr
        leasingAttributionSource
        lastInboundTimestamp
        lastOutboundTimestamp
      }
    }
  }
`

const deleteEndpointMutation = gql`
  mutation DeleteEndpoint($id: Int!, $taskId: Int!) {
    deleteEndpoint(id: $id, taskId: $taskId) {
      id
      addressEmail
      addressPhone
      isArchived
      endpoints {
        id
        addr
        releasedAddr
        leasingAttributionSource
        lastInboundTimestamp
        lastOutboundTimestamp
      }
    }
  }
`

const headers = ['Endpoint', 'Source', '']
const widths = ['160px', '180px', null]

interface Endpoint {
  id: number
  addr?: string | null
  releasedAddr?: string | null
  leasingAttributionSource: LeasingAttributionSource
  lastInboundTimestamp?: string | null
  lastOutboundTimestamp?: string | null
}

class EndpointsListImpl extends React.PureComponent<
  FetchEndpointsQueryVariables & QueryResult<FetchEndpointsQuery, FetchEndpointsQueryVariables>
> {
  private handlers = {}

  render() {
    const { data, released, taskId } = this.props
    if (data == null || data.task == null) {
      return null
    }
    const { endpoints } = data.task

    const cols = [
      (v: Endpoint) => <FormatAddr addr={(v.addr || v.releasedAddr)!} />,
      (v: Endpoint) => v.leasingAttributionSource,
      (v: Endpoint) =>
        released ? undefined : (
          <Link color="silver" onClick={memoize(this.handlers, v.id, () => () => this.deleteEndpoint(v))}>
            Del
          </Link>
        ),
    ]

    const showClipboardLinks = endpoints.length > 0 && !released

    return (
      <div>
        {showClipboardLinks && <Spacer height={4} />}
        {showClipboardLinks && (
          <BodyText>
            <Link onClick={this.copyNumbersToClipboard}>📋 Tracking numbers</Link>
            <Spacer width={16} />
            <Link onClick={this.copySourcesToClipboard}>📋 Marketing sources</Link>
          </BodyText>
        )}
        <Spacer height={16} />
        {endpoints.length > 0 && <Table rows={endpoints} columns={cols} headers={headers} widths={widths} />}
        {endpoints.length === 0 && <BodyText>No endpoints yet...</BodyText>}
        {!released && <Spacer height={16} />}
        {!released && <EndpointTypeahead taskId={taskId} endpoints={endpoints} />}
      </div>
    )
  }

  private deleteEndpoint({ id, leasingAttributionSource, lastInboundTimestamp, lastOutboundTimestamp }: Endpoint) {
    if (
      !confirm(`Are you sure you want to delete the endpoint for ${leasingAttributionSource}?
Last inbound was ${lastInboundTimestamp == null ? 'never' : dateMDYFmt(lastInboundTimestamp)}.
Last outbound was ${lastOutboundTimestamp == null ? 'never' : dateMDYFmt(lastOutboundTimestamp)}.`)
    ) {
      return
    }

    const { taskId } = this.props
    apolloWebClient
      .mutate({
        mutation: deleteEndpointMutation,
        variables: { id, taskId },
      })
      .then(() => addFeedback('Removed endpoint'))
      .catch((err: ApolloError) => handleError(err))
  }

  private copyNumbersToClipboard = () => {
    const { endpoints, displayName } = this.props.data!.task
    const csv = [displayName || '']
      .concat(
        endpoints.map(e =>
          e.leasingAttributionSource != null ? '+1' + (e.addr || e.releasedAddr)! : (e.addr || e.releasedAddr)!
        )
      )
      .join(',')
    this.copyToClipboard(csv, `Copied ${endpoints.length} #s`)
  }

  private copySourcesToClipboard = () => {
    const { endpoints } = this.props.data!.task
    const csv = endpoints
      .filter(e => e.leasingAttributionSource != null)
      .map(e => e.leasingAttributionSource!.toLowerCase())
      .join(',')
    this.copyToClipboard(csv, `Copied ${endpoints.length} sources`)
  }

  private copyToClipboard = (text: string, feedback: string) => {
    copyToClipboard(text)
    addFeedback(feedback)
  }
}

function FormatAddr({ addr }: { addr: string }) {
  const fmt = formatPhoneNumber(addr)
  return <span title={fmt}>{fmt}</span>
}

interface EndpointResult {
  source?: string
  addr?: string
}

class EndpointTypeahead extends React.PureComponent<{
  taskId: number
  endpoints: Endpoint[]
}> {
  render() {
    return (
      <TypeaheadBox<EndpointResult>
        loadFunc={this.loadBound}
        rowFunc={EndpointRow}
        onChoose={this.addEndpointBound}
        placeholder="Add endpoint..."
        showResultsWhenEmpty
      />
    )
  }

  private loadBound = (prefix: string) => {
    const { endpoints } = this.props

    const sources = Object.keys(LeasingAttributionSource)
    const addableSources = sources.filter(
      s => !endpoints.find(e => e.leasingAttributionSource === s && !(e.addr || e.releasedAddr)!.includes('@'))
    )
    const matchSources = addableSources.filter(s => s.toLowerCase().startsWith(prefix.toLowerCase())).slice(0, 10)

    const matches = matchSources.map(source => ({ source })) as EndpointResult[]

    const resp = validate.email().validate(prefix)
    if (resp.value) {
      matches.unshift({ addr: resp.value })
    }

    return Promise.resolve(matches)
  }

  private addEndpointBound = (result: EndpointResult) => {
    const { taskId } = this.props
    apolloWebClient
      .mutate({
        mutation: createEndpointMutation,
        variables: { taskId, leasingAttributionSource: result.source, addr: result.addr },
      })
      .then(() => addFeedback('Saved endpoint'))
      .catch((err: ApolloError) => handleError(err))
  }
}

function EndpointRow(props: EndpointResult) {
  const { source, addr } = props
  return (
    <IntroText>
      <EllipsisDiv>
        <BodyTextSpan>
          {addr || '(New #)'} &middot; {source || '(No source)'}
        </BodyTextSpan>
      </EllipsisDiv>
    </IntroText>
  )
}

function EndpointsList(props: FetchEndpointsQueryVariables) {
  return (
    <PollingQuery query={endpointsQuery} variables={props}>
      {(res: QueryResult<FetchEndpointsQuery, FetchEndpointsQueryVariables>) => (
        <EndpointsListImpl {...props} {...res} />
      )}
    </PollingQuery>
  )
}

type Tab = 'ACTIVE' | 'RELEASED'

export default class EndpointsSection extends React.PureComponent<{ taskId: number }, { tab: Tab }> {
  state = {
    tab: 'ACTIVE' as Tab,
  }

  render() {
    const { taskId } = this.props
    const { tab } = this.state

    return (
      <div>
        <H4>Endpoints</H4>
        <Spacer height={8} />
        <BodyText>Define which emails and phone numbers route to this task.</BodyText>
        <Spacer height={8} />
        <Button size="small" color="sky" filled={tab === 'ACTIVE'} onClick={this.setActive}>
          Active
        </Button>
        <Spacer width={8} />
        <Button size="small" color="sky" filled={tab === 'RELEASED'} onClick={this.setReleased}>
          Released
        </Button>
        <Spacer height={8} />
        <EndpointsList taskId={taskId} released={tab === 'RELEASED'} />
        <Spacer height={32} />
      </div>
    )
  }

  private setActive = () => this.setState({ tab: 'ACTIVE' })

  private setReleased = () => this.setState({ tab: 'RELEASED' })
}
